import { createContext, useContext, useEffect, useMemo, useReducer, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
    getAuth,
    signInWithEmailAndPassword,
    signInWithCustomToken,
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
} from "firebase/auth";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { FIREBASE_API, APPCHECK_SITE_KEY } from "../config";
import { setAppCheckInstance } from "../services/appCheck";

const firebaseApp = initializeApp(FIREBASE_API);
if (APPCHECK_SITE_KEY) {
    setAppCheckInstance(
        initializeAppCheck(firebaseApp, {
            provider: new ReCaptchaV3Provider(APPCHECK_SITE_KEY),
            isTokenAutoRefreshEnabled: true,
        })
    );
}
const AUTH = getAuth(firebaseApp);
const googleProvider = new GoogleAuthProvider();

const initialState = {
    isInitialized: false,
    isAuthenticated: false,
    user: null,
};

const reducer = (state, action) => {
    switch (action.type) {
        case "INITIALISE":
            return {
                ...state,
                isInitialized: true,
                isAuthenticated: action.payload.isAuthenticated,
                user: action.payload.user,
            };
        default:
            return state;
    }
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(AUTH, async (firebaseUser) => {
            if (firebaseUser) {
                // Gost dobija custom token (isAnonymous je false na klijentu), ali server
                // mu postavlja claim `anonymous: true`. Zato pravi nalog razlikujemo po
                // tom claim-u, a ne po firebaseUser.isAnonymous. Gost NIJE „ulogovan".
                let isGuest = firebaseUser.isAnonymous;
                try {
                    const tokenResult = await firebaseUser.getIdTokenResult();
                    if (tokenResult?.claims?.anonymous === true) isGuest = true;
                } catch {
                    // Ako čitanje claim-a padne, ostajemo na firebaseUser.isAnonymous.
                }
                dispatch({
                    type: "INITIALISE",
                    payload: {
                        isAuthenticated: !isGuest,
                        user: {
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            displayName: firebaseUser.displayName,
                            photoURL: firebaseUser.photoURL,
                            isAnonymous: firebaseUser.isAnonymous,
                            isGuest,
                        },
                    },
                });
            } else {
                dispatch({
                    type: "INITIALISE",
                    payload: { isAuthenticated: false, user: null },
                });
            }
        });
        return () => unsubscribe();
    }, []);

    const login = useCallback(async (email, password) => {
        return signInWithEmailAndPassword(AUTH, email, password);
    }, []);

    const loginWithGoogle = useCallback(async () => {
        return signInWithPopup(AUTH, googleProvider);
    }, []);

    const loginWithCustomToken = useCallback(async (token) => {
        return signInWithCustomToken(AUTH, token);
    }, []);

    const register = useCallback(async (email, password, phone) => {
        const userCredential = await createUserWithEmailAndPassword(AUTH, email, password);
        if (phone) {
            await updateProfile(userCredential.user, {
                displayName: phone,
            });
        }
        return userCredential;
    }, []);

    const logout = useCallback(async () => {
        await signOut(AUTH);
    }, []);

    const value = useMemo(
        () => ({
            ...state,
            method: "firebase",
            login,
            loginWithGoogle,
            loginWithCustomToken,
            register,
            logout,
        }),
        [state, login, loginWithGoogle, loginWithCustomToken, register, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuthContext must be used within AuthProvider");
    return context;
};

export { AUTH };
